import type { JSX } from 'react'
import { useSupportReadOnly } from '@/auth/support'

/**
 * SUP-40 — the warning screen 08 draws **in the tenant body**, under the red bar.
 *
 * `superadmin.html:571-574` puts a second, quieter notice inside the application
 * frame saying what is and is not possible right now: *"Actions are disabled in
 * read-only impersonation — approving, publishing, settings blocked; write needs
 * a second staff approval, capped at 15 minutes."* The banner above it says a
 * support session is open; this says what that means for the next click.
 *
 * **Two notices rather than one longer banner, and that is the design's call.**
 * The red bar is written for the *customer* — it explains what is happening to
 * their account and never mentions grants or approvals. This one is written for
 * the *staff member*, in their vocabulary, and it belongs in the body where their
 * work is rather than in the customer's notice. Merging them would put internal
 * process language into the sentence a customer reads over their own screen.
 *
 * Rendered by `AppShell` above the routed area, so it cannot be forgotten by a
 * screen and cannot disagree with the banner about which session is open.
 * `null` on every ordinary session and on a write grant — see `useSupportReadOnly`
 * on why the second of those is not an oversight.
 */
export function SupportReadOnlyNotice(): JSX.Element | null {
  if (!useSupportReadOnly()) return null
  return (
    <div className="ar-alert ar-alert--warning" role="status">
      <span className="ar-alert__icon" aria-hidden="true">
        ⚠
      </span>
      <div className="ar-alert__body">
        <strong>Actions are disabled in read-only impersonation</strong>
        Approving, publishing and settings are blocked. Write access needs a second staff approval
        and is capped at 15 minutes — ask for it from the banner above. The server refuses these
        regardless of what this screen shows.
      </div>
    </div>
  )
}
